import type { CurrentReview } from '@shared/domain/review';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import ShareIcon from '@mui/icons-material/Share';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';
import AuthorBadge from '@shared/components/AuthorBadge';
import CompanyLogo from '@shared/components/CompanyLogo';
import ReactionCounts from './ReactionCounts';
import {
  ActionButton,
  ActionsRow,
  BackToTopButton,
  BannerCard,
  HeaderRow,
  LogoSection,
  MetaInfo,
  ReactionCard,
  SectionTab,
  StickyBar,
  StickyCompanyName,
  StickyTab,
  StickyTabsContainer,
  TabsRow,
  TitleSection,
} from './ReviewBanner.styles';

interface ReviewBannerDesktopProps {
  companyId?: number;
  companyName: string;
  companyUrl?: string;
  currentReview?: CurrentReview;
  sections: { key: string; title: string }[];
  isSticky: boolean;
  isEditMode: boolean;
  isAdmin: boolean;
  isOwnReview: boolean;
  canEditReview: boolean;
  reactionCounts?: { likeDislikeCount: number; likePercentage: number };
  isFavorite: boolean;
  isTogglingFavorite: boolean;
  onCompanyClick: () => void;
  onScrollToSection: (id: string) => void;
  onAskQuestion: () => void;
  onShareDealRetro: () => void;
  onRequestConsultation: () => void;
  onToggleEditMode: () => void;
  onFavoriteToggle: () => void;
}

export const ReviewBannerDesktop: React.FC<ReviewBannerDesktopProps> = ({
  companyId,
  companyName,
  companyUrl,
  currentReview,
  sections,
  isSticky,
  isEditMode,
  isAdmin,
  isOwnReview,
  canEditReview,
  reactionCounts,
  isFavorite,
  isTogglingFavorite,
  onCompanyClick,
  onScrollToSection,
  onAskQuestion,
  onShareDealRetro,
  onRequestConsultation,
  onToggleEditMode,
  onFavoriteToggle,
}) => {
  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isSticky) {
    return (
      <>
        <StickyBar
          key="sticky-bar"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          <CompanyLogo
            companyId={companyId}
            companyName={companyName}
            companyUrl={companyUrl}
            alt={`${companyName || ''} logo`}
            onClick={onCompanyClick}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          />
          <StickyCompanyName onClick={onCompanyClick}>
            {companyName}
          </StickyCompanyName>
          <StickyTabsContainer>
            {sections.map(({ key, title }) => (
              <StickyTab
                key={key}
                onClick={() => onScrollToSection(key)}
              >
                {title}
              </StickyTab>
            ))}
            {!isEditMode && (
              <StickyTab onClick={() => onScrollToSection('qa')}>
                Q&A
              </StickyTab>
            )}
          </StickyTabsContainer>
        </StickyBar>

        <BackToTopButton
          key="back-to-top"
          onClick={handleBackToTop}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.25 }}
          aria-label="Back to top"
        >
          ↑
        </BackToTopButton>
      </>
    );
  }

  return (
    <BannerCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <HeaderRow>
        <LogoSection>
          <CompanyLogo
            companyId={companyId}
            companyName={companyName}
            companyUrl={companyUrl}
            alt={`${companyName || ''} logo`}
            onClick={onCompanyClick}
            sx={{
              'width': 80,
              'height': 80,
              'borderRadius': '12px',
              'boxShadow': '0 2px 8px rgba(0,0,0,0.08)',
              'cursor': 'pointer',
              'transition': 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.03)',
              },
            }}
          />
        </LogoSection>

        <TitleSection>
          <Typography
            variant="h5"
            sx={{
              'fontWeight': 600,
              'color': '#2a2018',
              'cursor': 'pointer',
              'fontSize': '1.5rem',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={onCompanyClick}
          >
            {companyName}
          </Typography>
          <MetaInfo>
            {currentReview?.userId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Author:
                </Typography>
                <AuthorBadge
                  userId={currentReview.userId}
                  user={currentReview.user}
                  size="small"
                  avatarOnly={true}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Department:
              </Typography>
              <Chip
                label={currentReview?.dealArea || 'N/A'}
                size="small"
                sx={{
                  bgcolor: 'rgba(156, 39, 176, 0.08)',
                  color: '#7B1FA2',
                  fontWeight: 500,
                  border: '1px solid rgba(156, 39, 176, 0.2)',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Year:
              </Typography>
              <Chip
                label={currentReview?.dealDate || 'N/A'}
                size="small"
                sx={{
                  bgcolor: 'rgba(192, 90, 58, 0.08)',
                  color: '#c05a3a',
                  fontWeight: 500,
                  border: '1px solid rgba(192, 90, 58, 0.2)',
                }}
              />
            </Box>
          </MetaInfo>
        </TitleSection>

        <ReactionCard>
          <ReactionCounts
            likeDislikeCount={
              reactionCounts?.likeDislikeCount
              ?? currentReview?.likeDislikeCount
              ?? 0
            }
            likePercentage={
              reactionCounts?.likePercentage
              ?? currentReview?.likePercentage
              ?? 0
            }
          />
        </ReactionCard>
      </HeaderRow>

      <TabsRow>
        {sections.map(({ key, title }) => (
          <SectionTab
            key={key}
            onClick={() => onScrollToSection(key)}
          >
            {title}
          </SectionTab>
        ))}
        {!isEditMode && (
          <SectionTab onClick={() => onScrollToSection('qa')}>
            Q&A
          </SectionTab>
        )}
      </TabsRow>

      <ActionsRow>
        <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
          <IconButton
            onClick={onFavoriteToggle}
            disabled={isTogglingFavorite}
            sx={{
              'color': isFavorite ? 'warning.main' : 'action.active',
              '&:hover': { color: 'warning.main' },
            }}
          >
            {isFavorite ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>
        </Tooltip>

        {!isOwnReview && (
          <>
            <ActionButton onClick={onAskQuestion}>
              <QuestionAnswerOutlinedIcon sx={{ fontSize: 18 }} />
              Ask a Question
            </ActionButton>
            <ActionButton onClick={onRequestConsultation}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
              Request Consultation
            </ActionButton>
          </>
        )}
        <ActionButton onClick={onShareDealRetro}>
          <ShareIcon sx={{ fontSize: 18 }} />
          Share Deal Retro
        </ActionButton>
        {canEditReview && (
          <ActionButton onClick={onToggleEditMode}>
            <EditIcon sx={{ fontSize: 18 }} />
            {isEditMode ? 'View' : 'Edit'}
          </ActionButton>
        )}
      </ActionsRow>
    </BannerCard>
  );
};
